// Main Survey Application Logic
class SurveyApp {
    constructor() {
        this.currentStep = 1;
        this.totalSteps = 15;
        this.isSubmitting = false;
        this.apiEndpoint = 'https://ddgq3as2v5.execute-api.us-east-1.amazonaws.com/prod/nosia-survey'; // Placeholder API endpoint

        this.init();
    }

    init() {
        // Check if survey is already completed
        if (surveyStorage.isCompleted()) {
            this.showSuccessPage();
            return;
        }

        // Setup auto-save functionality
        surveyStorage.setupAutoSave();

        // Restore previous session if exists
        this.restoreSession();

        // Setup form submission handler
        this.setupFormSubmission();

        // Setup keyboard navigation
        this.setupKeyboardNavigation();

        // Setup auto-navigation for radio buttons
        this.setupAutoNavigation();

        // Update progress bar
        this.updateProgress();

        // Setup NewRelic tracking
        this.trackPageView();
    }

    // Restore previous session data
    restoreSession() {
        const savedStep = surveyStorage.getCurrentStep();
        if (savedStep > 1 && savedStep <= this.totalSteps) {
            this.currentStep = savedStep;
            this.showStep(this.currentStep);
        }

        // Restore form data
        surveyStorage.restoreFormState();
    }

    // Navigate to next step
    nextStep() {
        if (this.currentStep < this.totalSteps) {
            this.currentStep++;
            this.showStep(this.currentStep);
            this.updateProgress();
            surveyStorage.saveCurrentStep(this.currentStep);
            this.trackStepView();
        }
    }

    // Navigate to previous step
    previousStep() {
        if (this.currentStep > 1) {
            this.currentStep--;
            this.showStep(this.currentStep);
            this.updateProgress();
            surveyStorage.saveCurrentStep(this.currentStep);
            this.trackStepView();
        }
    }

    // Show specific step
    showStep(stepNumber) {
        // Hide all steps
        document.querySelectorAll('.step').forEach(step => {
            step.classList.remove('active');
        });

        // Show current step
        const currentStepElement = document.getElementById(`step${stepNumber}`);
        if (currentStepElement) {
            currentStepElement.classList.add('active');
        }

        // Scroll to top
        window.scrollTo(0, 0);
    }

    // Update progress bar
    updateProgress() {
        const progressFill = document.getElementById('progressFill');
        const progressText = document.getElementById('progressText');

        if (progressFill && progressText) {
            const percentage = (this.currentStep / this.totalSteps) * 100;
            progressFill.style.width = `${percentage}%`;
            progressText.textContent = `Step ${this.currentStep} of ${this.totalSteps}`;
        }
    }

    // Setup form submission
    setupFormSubmission() {
        const form = document.getElementById('surveyForm');
        if (form) {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.submitSurvey();
            });
        }
    }

    // Setup keyboard navigation
    setupKeyboardNavigation() {
        document.addEventListener('keydown', (e) => {
            // Don't interfere with typing in textarea
            if (e.target.tagName === 'TEXTAREA') {
                return;
            }

            if (e.key === 'Enter' && !e.shiftKey) {
                const activeStep = document.querySelector('.step.active');
                if (activeStep) {
                    const nextButton = activeStep.querySelector('.btn-primary:not(:disabled)');
                    if (nextButton) {
                        e.preventDefault();
                        nextButton.click();
                    }
                }
            }
        });
    }

    // Setup auto-navigation for radio buttons
    setupAutoNavigation() {
        const handleRadioSelection = (e) => {
            // Only handle radio button interactions
            if (e.target.type === 'radio') {
                // Save the selection first
                surveyStorage.saveField(e.target.name, e.target.value);

                // Get the current active step
                const activeStep = document.querySelector('.step.active');
                if (!activeStep) return;

                // Skip auto-navigation for step 15 (final step with textarea)
                const stepId = activeStep.id;
                if (stepId === 'step15') return;

                // Add a small delay for better user experience (let them see the selection)
                setTimeout(() => {
                    // Check if we're still on the same step (user didn't navigate away)
                    const currentActiveStep = document.querySelector('.step.active');
                    if (currentActiveStep && currentActiveStep.id === stepId) {
                        // Normal navigation to next step for all cases
                        this.nextStep();
                    }
                }, 400); // 400ms delay for better UX
            }
        };

        // Listen for change events (when value actually changes)
        document.addEventListener('change', handleRadioSelection);

        // Listen for mousedown/touchstart events on radio button labels
        // This handles the case where user clicks/taps on already selected option
        document.addEventListener('mousedown', (e) => {
            // Check if clicked element is a label containing a radio button
            if (e.target.classList.contains('option-label') || e.target.closest('.option-label')) {
                const label = e.target.classList.contains('option-label') ? e.target : e.target.closest('.option-label');
                const radio = label.querySelector('input[type="radio"]');

                if (radio && radio.checked) {
                    // Radio is already selected, trigger navigation
                    handleRadioSelection({ target: radio });
                }
            }
        });

        // Listen for touchstart events for mobile devices
        document.addEventListener('touchstart', (e) => {
            // Check if touched element is a label containing a radio button
            if (e.target.classList.contains('option-label') || e.target.closest('.option-label')) {
                const label = e.target.classList.contains('option-label') ? e.target : e.target.closest('.option-label');
                const radio = label.querySelector('input[type="radio"]');

                if (radio && radio.checked) {
                    // Radio is already selected, trigger navigation
                    handleRadioSelection({ target: radio });
                }
            }
        }, { passive: true });
    }

    // Submit survey data
    async submitSurvey(isEarlyExit = false) {
        if (this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        this.showLoadingOverlay();

        try {
            const formData = surveyStorage.getFormattedData();

            // Add early exit flag if applicable. No longer used.
            if (isEarlyExit) {
                formData.early_exit = true;
                formData.early_exit_reason = 'screening_question_yes';
            }

            // Submit to API
            const apiSuccess = await this.submitToAPI(formData);
            if (apiSuccess || isEarlyExit) {
                // Track in NewRelic
                this.trackSubmission(formData);
                // Mark as completed
                surveyStorage.markCompleted();

                // Redirect to success page
                this.redirectToSuccess();
            } else {
                throw new Error('API submission failed');
            }

        } catch (error) {
            console.error('Survey submission error:', error);
            this.handleSubmissionError(error);
        } finally {
            this.isSubmitting = false;
            this.hideLoadingOverlay();
        }
    }

    // Submit data to API
    async submitToAPI(data) {
        try {
            const response = await fetch(this.apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(data)
            });

            if (response.ok) {
                const result = await response.json();
                return true;
            } else {
                console.error('API submission failed:', response.status, response.statusText);
                return false;
            }
        } catch (error) {
            console.error('API submission error:', error);
            return true;
        }
    }

    // Track submission in NewRelic
    trackSubmission(data) {
        if (typeof newrelic !== 'undefined') {
            newrelic.addPageAction('survey_submission', {
                Value: data.tkt,
                eventName: JSON.stringify(data)
            });
        }
    }

    // Track page view in NewRelic
    trackPageView() {
        if (typeof newrelic !== 'undefined') {
            newrelic.addPageAction('survey_start', {
                Value: surveyStorage.getTktFromUrl()
            });
        }
    }

    // Track step view in NewRelic
    trackStepView() {
        if (typeof newrelic !== 'undefined') {
            newrelic.addPageAction('survey_step_view', {
                eventName: this.currentStep,
                Value: surveyStorage.getTktFromUrl()
            });
        }
    }

    // Handle submission errors
    handleSubmissionError(error) {
        alert('There was an error submitting your survey. Please try again or contact support if the problem persists.');
        console.error('Submission error details:', error);
    }

    // Show loading overlay
    showLoadingOverlay() {
        const overlay = document.getElementById('loadingOverlay');
        if (overlay) {
            overlay.classList.add('active');
        }
    }

    // Hide loading overlay
    hideLoadingOverlay() {
        const overlay = document.getElementById('loadingOverlay');
        if (overlay) {
            overlay.classList.remove('active');
        }
    }

    // Show success page
    showSuccessPage() {
        // Use the global showSuccessPage function from index.html
        if (typeof window.showSuccessPage === 'function') {
            window.showSuccessPage();
        }
    }

    // Redirect to success page (legacy method, now calls showSuccessPage)
    redirectToSuccess() {
        this.showSuccessPage();
    }
}

// Global functions for HTML onclick handlers
function nextStep() {
    if (window.surveyApp) {
        window.surveyApp.nextStep();
    }
}

function previousStep() {
    if (window.surveyApp) {
        window.surveyApp.previousStep();
    }
}

// Handle screening question change (legacy function - now handled by auto-navigation)
function handleScreeningChange(input) {
    // Auto-navigation will handle this, but keep for backward compatibility
    enableNextButton('step2Next');
}

// Enable next button when option is selected (legacy function - now handled by auto-navigation)
function enableNextButton(buttonId) {
    const button = document.getElementById(buttonId);
    if (button) {
        button.disabled = false;
    }
}

// Enable submit button when textarea has content
function enableSubmitButton() {
    const textarea = document.getElementById('openFeedback');
    const submitBtn = document.getElementById('submitBtn');

    if (textarea && submitBtn) {
        // Enable submit button regardless of textarea content
        // User can submit empty feedback
        submitBtn.disabled = false;
    }
}

// Validate token function
function validateToken() {
    const urlParams = new URLSearchParams(window.location.search);
    const tkt = urlParams.get('tkt');
    const chk = urlParams.get('chk');

    if (!tkt || tkt.trim() === '' || !chk || chk.trim() === '') {
        // Show error message for malformed URL
        showTokenError();
        return false;
    }

    return true;
}

// Show token error message
function showTokenError() {
    // Hide the survey form and progress bar
    const surveyForm = document.querySelector('.survey-form');
    const progressContainer = document.querySelector('.progress-container');

    if (surveyForm) surveyForm.style.display = 'none';
    if (progressContainer) progressContainer.style.display = 'none';

    // Create and show error message
    const errorContainer = document.createElement('div');
    errorContainer.className = 'error-container';
    errorContainer.innerHTML = `
        <div class="error-content">
            <div class="error-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#dc3545"/>
                    <path d="M15 9l-6 6M9 9l6 6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
            <h1 class="error-title">Invalid Survey Link</h1>
            <p class="error-message">The URL you used to access this survey appears to be malformed or incomplete.</p>
            <div class="error-details">
                <p>Please ensure you're using the complete link provided in your email.</p>
                <p>If you believe this is an error, please contact our customer service team.</p>
            </div>
        </div>
    `;

    // Insert error container into the survey container
    const surveyContainer = document.querySelector('.survey-container');
    if (surveyContainer) {
        surveyContainer.appendChild(errorContainer);
    }
}

// Initialize app when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    // Validate token before initializing the app
    if (!validateToken()) {
        return; // Stop initialization if token is invalid
    }
    if (window.newrelic) {
        window.newrelic.setCustomAttribute("custom/persistent_sessionid", surveyStorage.getTktFromUrl());
    }
    // Create global app instance
    window.surveyApp = new SurveyApp();

    // Enable submit button on page load for step 15
    setTimeout(() => {
        enableSubmitButton();
    }, 100);
});

// Handle page visibility changes
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
        // Save current state when user leaves page
        const currentStep = document.querySelector('.step.active');
        if (currentStep && window.surveyApp) {
            const stepNumber = parseInt(currentStep.id.replace('step', ''));
            surveyStorage.saveCurrentStep(stepNumber);
        }
    }
});

// Prevent accidental page refresh
window.addEventListener('beforeunload', (e) => {
    if (window.surveyApp && !surveyStorage.isCompleted() && window.surveyApp.currentStep > 1) {
        e.preventDefault();
        // Modern browsers will show their own confirmation dialog
        // No need to set returnValue (deprecated) or return a value
    }
});
